import React, { useEffect, useState } from "react";
import { useTable } from "@/lib/store";
import { api, API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Kicker, SectionHead, Field, Pick } from "@/components/kit";
import { ROMAN, relTime } from "@/lib/util";
import { Upload, Download, Trash2, FileText, Pin } from "lucide-react";

const FOLDERS = ["Repository", "Policy", "Agreements", "Retreat", "Chapter"];

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function Drive() {
  const { me, meetings, yearId, memberById, canWrite } = useTable();
  const [files, setFiles] = useState([]);
  const [folder, setFolder] = useState("Repository");
  const [meetingId, setMeetingId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    const r = await api.get("/drive", { params: { yearId } });
    setFiles(r.data);
  };
  useEffect(() => { if (yearId) load(); }, [yearId]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("yearId", yearId);
    fd.append("meetingId", meetingId);
    fd.append("folder", folder);
    fd.append("notes", notes);
    try {
      await api.post("/drive", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotes("");
      await load();
      toast.success("Filed to the drive.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const download = async (f) => {
    try {
      const r = await api.get(`/drive/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const folders = ["All", ...Array.from(new Set(files.map((f) => f.folder || "Repository")))];
  const shown = files.filter((f) => filter === "All" || (f.folder || "Repository") === filter);

  return (
    <div data-testid="drive-page">
      <PageHead
        kicker="Forum drive"
        title="Drive"
        sub="The forum's permanent file room — policy, agreements and everything attached to a chapter."
      />

      {canWrite && (
        <Card className="mb-6" data-testid="drive-upload-card">
          <SectionHead>Add a file</SectionHead>
          <div className="grid sm:grid-cols-2 gap-4">
            <Pick label="Folder" options={FOLDERS} value={folder} data-testid="drive-folder" onChange={(e) => setFolder(e.target.value)} />
            <Pick
              label="Attach to chapter (optional)"
              data-testid="drive-meeting"
              value={meetingId}
              onChange={(e) => setMeetingId(e.target.value)}
              options={[{ value: "", label: "None — general repository" },
                ...meetings.map((m) => ({ value: m.id, label: `Chapter ${ROMAN[m.num - 1]}${m.title ? ` — ${m.title}` : ""}` }))]}
            />
          </div>
          <div className="mt-4">
            <Field label="Note (optional)" value={notes} data-testid="drive-notes" onChange={(e) => setNotes(e.target.value)} />
          </div>
          <label className="btn-ink mt-4 inline-flex cursor-pointer" data-testid="drive-upload">
            <Upload size={14} /> {busy ? "Uploading…" : "Choose file"}
            <input
              type="file"
              className="hidden"
              data-testid="drive-file-input"
              disabled={busy}
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
          <p className="lede !text-[12px] mt-2">Up to 25 MB. PDF, Word, Excel, images and text.</p>
        </Card>
      )}

      {folders.length > 2 && (
        <div className="flex gap-2 flex-wrap mb-5">
          {folders.map((f) => (
            <button key={f} className={`chip ${filter === f ? "chip-active" : ""}`} data-testid={`drive-filter-${f}`} onClick={() => setFilter(f)}>
              {f}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <Empty line="The drive is empty. Upload the policy to begin the record." testId="drive-empty" />
      ) : (
        <Card className="!p-0 overflow-hidden divide-hair">
          {shown.map((f) => {
            const ch = meetings.find((m) => m.id === f.meetingId);
            const by = memberById(f.uploadedById);
            return (
              <div key={f.id} className="flex flex-wrap items-start sm:items-center gap-x-4 gap-y-3 px-5 py-4" data-testid={`drive-file-${f.id}`}>
                <span
                  className="flex items-center justify-center rounded-md shrink-0"
                  style={{ width: 34, height: 34, background: "var(--accent-wash)", color: "var(--brass)" }}
                >
                  <FileText size={15} />
                </span>
                <div className="min-w-0 flex-1 basis-[calc(100%-3.5rem)] sm:basis-auto">
                  <div className="text-[14px] font-medium truncate" style={{ color: "var(--ink)" }}>{f.name}</div>
                  <div className="text-[12px] mt-1 flex gap-2 flex-wrap items-center" style={{ color: "var(--ink-mute)" }}>
                    <span>{kb(f.size)}</span>
                    <span>· {by?.name || "Member"}</span>
                    <span>· {relTime(f.createdAt)}</span>
                    {f.folder && <Chip>{f.folder}</Chip>}
                    {ch && <Chip color="var(--brass)">Chapter {ROMAN[ch.num - 1]}</Chip>}
                  </div>
                  {f.notes && <div className="text-[12.5px] mt-1.5" style={{ color: "var(--ink-mute)" }}>{f.notes}</div>}
                </div>
                <div className="flex items-center gap-2 ml-auto shrink-0">
                {f.pinned && <Chip color="var(--brass)" testId={`drive-pinned-${f.id}`}>Pinned</Chip>}
                {me.isAdmin && canWrite && (
                  <button
                    className="btn-quiet !min-h-0 !px-2.5 !py-2"
                    data-testid={`drive-pin-${f.id}`}
                    title={f.pinned ? "Unpin" : "Pin to the top"}
                    onClick={async () => { await api.put(`/drive/${f.id}/pin`); await load(); }}
                  >
                    <Pin size={13} style={f.pinned ? { color: "var(--brass-bright)" } : undefined} />
                  </button>
                )}
                <button className="btn-quiet !min-h-0 !px-2.5 !py-2" data-testid={`drive-download-${f.id}`} onClick={() => download(f)}>
                  <Download size={13} />
                </button>
                {canWrite && (me.isAdmin || f.uploadedById === me.memberId) && (
                  <button
                    className="btn-quiet !min-h-0 !px-2.5 !py-2"
                    data-testid={`drive-delete-${f.id}`}
                    onClick={async () => { await api.delete(`/drive/${f.id}`); await load(); }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
